import type { Response as PapiResponse } from "@ascent/papi-core";

export type EnrichResultContext = {
  query: string;
  results: Array<{ url: string; title: string; desc: string }>;
  language: string;
  country_code: string;
  /**
   * Preferred clock format for timezone enrichment (`world` = 24h, `us` = 12h).
   * Typically derived from the browser Intl hour cycle; falls back to country.
   */
  date_and_time_format?: "world" | "us";
};

/**
 * URL-encode the search query for POST /v1/enrich. EE decodes with
 * `unquote_plus`, so literal `+` in math expressions must be sent as `%2B`.
 */
export function encodeEnrichQuery(query: string): string {
  return encodeURIComponent(query.trim());
}

/** EE request body shape — query is encoded; use plain text elsewhere. */
export function enrichResultContextForRequest(
  context: EnrichResultContext,
): EnrichResultContext {
  return { ...context, query: encodeEnrichQuery(context.query) };
}

export type EnrichConfigToken = {
  /**
   * Per-module EE settings. Keys are `{module}_settings`.
   * Provider credentials are never sent: only `secret_name` (Secrets Manager
   * path). EE loads the key server-side. expander / priceline may omit
   * `secret_name` (EE has a default path). Finnhub always sends `secret_name`.
   * Non-secret extras (wikipedia `ua_contact`, timezone `date_and_time_format`,
   * openweathermap `temperature_unit` / `date_time_format`) are allowlisted.
   */
  settings: Record<string, Record<string, unknown>>;
  slots: Record<string, string[]>;
};

/** Non-secret keys EE still accepts in `{module}_settings` (never API keys). */
const SETTINGS_EXTRA_KEYS: Record<string, readonly string[]> = {
  wikipedia_settings: ["ua_contact"],
  timezone_settings: ["date_and_time_format"],
  openweathermap_settings: ["temperature_unit", "date_time_format"],
};

/**
 * `secret_name` names a Secrets Manager path that EE loads server-side. The
 * token we decode here is not necessarily the one PAPI issued: POST /enrich is
 * public (publisher pages call it cross-origin) and the browser round-trips the
 * token, so a caller can forge one. Confine the value to EE's own namespace and
 * to the exact shape PAPI issues, which makes traversal and arbitrary secret
 * selection impossible. Provider and tenant segments stay open so onboarding a
 * partner doesn't need a renderer release.
 */
const SECRET_NAME_PATTERN =
  /^enrichment-engine\/[A-Za-z0-9][A-Za-z0-9_-]*\/[A-Za-z0-9][A-Za-z0-9_-]*\/env=[A-Za-z0-9_-]+\/auth=[A-Za-z0-9_-]+$/;

/** Empty string when the path is not an EE secret path we are willing to forward. */
export function safeSecretName(value: string): string {
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > 200) return "";
  return SECRET_NAME_PATTERN.test(trimmed) ? trimmed : "";
}

/**
 * Keep `secret_name` (and a few non-secret extras). Drop raw API keys so they
 * never appear in POST /v1/enrich even if PAPI put them in ee_token.
 */
function sanitizeFinnhubSettings(
  value: Record<string, unknown>,
  out: Record<string, unknown>,
): void {
  const enrichMode = stringField(value, "enrich_mode");
  if (enrichMode === "ticker" || enrichMode === "profile") {
    out.enrich_mode = enrichMode;
  }
  const modes = asRecord(value.slot_modes);
  if (!modes) return;
  const slotModes: Record<string, string> = {};
  for (const [slot, mode] of Object.entries(modes)) {
    if (mode === "ticker" || mode === "profile") slotModes[slot] = mode;
  }
  if (Object.keys(slotModes).length > 0) out.slot_modes = slotModes;
}

export function sanitizeModuleSettings(
  key: string,
  value: Record<string, unknown>,
): Record<string, unknown> | undefined {
  const out: Record<string, unknown> = {};
  const secretName = safeSecretName(stringField(value, "secret_name"));
  if (secretName) out.secret_name = secretName;
  for (const extra of SETTINGS_EXTRA_KEYS[key] ?? []) {
    if (extra === "date_and_time_format") {
      const format = normalizeDateAndTimeFormat(value[extra]);
      if (format) out[extra] = format;
      continue;
    }
    const v = stringField(value, extra);
    if (v) out[extra] = v;
  }
  if (key === "finnhub_settings") sanitizeFinnhubSettings(value, out);
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * Timezone module (and future modules): EE SettingsModel keys that must appear
 * under `{module}_settings` even when PAPI sends `{}` (sanitizer would otherwise
 * drop empty objects). Defaults satisfy EE validation so the module is not
 * rejected as `module_settings_missing`.
 *
 * Provider credentials are never synthesized here. `finnhub` and
 * `finnhub_profile` must carry their own sanitized settings from PAPI.
 */
const MODULE_SETTINGS_DEFAULTS: Record<
  string,
  { settingsKey: string; defaults: Record<string, unknown> }
> = {
  timezone: {
    settingsKey: "timezone_settings",
    defaults: { date_and_time_format: "world" },
  },
};

/** Timezone module: normalize EE clock preference; unknown values → undefined. */
export function normalizeDateAndTimeFormat(
  value: unknown,
): "world" | "us" | undefined {
  if (value === "world" || value === "us") return value;
  if (typeof value !== "string") return undefined;
  const v = value.trim().toLowerCase();
  if (v === "world" || v === "us") return v;
  return undefined;
}

/**
 * Timezone module: infer `world` (24h) vs `us` (12h) from locale / country when
 * the browser hour-cycle signal is unavailable (e.g. server-side). US → us;
 * else world.
 */
export function dateAndTimeFormatFromCountry(
  countryCode: string | undefined,
): "world" | "us" {
  return (countryCode || "").trim().toUpperCase() === "US" ? "us" : "world";
}

/**
 * Clock format from PAPI `user_defaults` when an explicit 2-letter region is set.
 * Undefined when the region is missing / "all" so callers keep the token default.
 */
export function dateAndTimeFormatFromResponse(
  response: PapiResponse | undefined,
): "world" | "us" | undefined {
  const userDefaults = asRecord(response?.user_defaults);
  const countryRaw = stringField(
    userDefaults,
    "preferred_results_region",
    "country_code",
  );
  const explicitCountry =
    countryRaw.length === 2 ? countryRaw.toUpperCase() : undefined;
  if (!explicitCountry) return undefined;
  return dateAndTimeFormatFromCountry(explicitCountry);
}

export type EnsureEnrichmentSettingsOptions = {
  /** Timezone module: browser clock preference; used only when the token omits the field. */
  dateAndTimeFormat?: "world" | "us";
};

export type EnrichmentSlotDef = {
  name: string;
  modules: string[];
  /** Region that owns this placeholder (for debugging). */
  region: string;
};

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

function stringField(
  obj: Record<string, unknown> | undefined,
  ...keys: string[]
): string {
  if (!obj) return "";
  for (const key of keys) {
    const v = obj[key];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return "";
}

function normalizeModules(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (m): m is string => typeof m === "string" && m.trim().length > 0,
  );
}

/** True when a region group is an Enrichment Engine slot placeholder. */
export function isEnrichmentSlotGroup(group: unknown): boolean {
  const g = asRecord(group);
  return (
    g?.backend === "enrichment_engine" && g?.result_type === "enrichment_slot"
  );
}

/**
 * Opaque EE config token from PAPI (`page_options.enrichment_engine.ee_token`).
 * Encodes `{ settings, slots }` (typically base64 JSON).
 */
export function extractEeToken(
  response: PapiResponse | undefined,
): string | undefined {
  const pageOptions = asRecord(response?.page_options);
  const ee = asRecord(pageOptions?.enrichment_engine);
  const token = ee?.ee_token;
  return typeof token === "string" && token.trim() ? token.trim() : undefined;
}

/** UTF-8 base64 decode that works in Node and browser bundles. */
function decodeBase64Utf8(value: string): string {
  const binary = atob(value);
  const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
  return new TextDecoder("utf-8").decode(bytes);
}

/** Decode a base64 (or raw JSON) ee_token into a config_token object. */
export function decodeEeToken(token: string): EnrichConfigToken | undefined {
  const trimmed = token.trim();
  if (!trimmed) return undefined;

  let jsonText = trimmed;
  try {
    // Prefer base64 decode; fall back to raw JSON if already an object string.
    if (!trimmed.startsWith("{")) {
      jsonText = decodeBase64Utf8(trimmed);
    }
    const parsed = JSON.parse(jsonText) as unknown;
    const obj = asRecord(parsed);
    if (!obj) return undefined;

    const slotsRaw = asRecord(obj.slots) ?? {};
    const slots: Record<string, string[]> = {};
    for (const [name, mods] of Object.entries(slotsRaw)) {
      const modules = normalizeModules(mods);
      if (name && modules.length > 0) slots[name] = modules;
    }
    if (Object.keys(slots).length === 0) return undefined;

    const settingsRaw = asRecord(obj.settings) ?? {};
    const settings: Record<string, Record<string, unknown>> = {};
    for (const [key, value] of Object.entries(settingsRaw)) {
      const nested = asRecord(value);
      if (!nested) continue;
      const sanitized = sanitizeModuleSettings(key, nested);
      if (sanitized) settings[key] = sanitized;
    }

    return { settings, slots };
  } catch {
    return undefined;
  }
}

/**
 * Ensure wikipedia modules have a UA contact when PAPI's token omits settings.
 * Ensure timezone modules keep `timezone_settings` (empty `{}` from PAPI is
 * otherwise stripped by sanitize). Provider credentials are not invented for
 * missing settings: the browser token is unsigned and therefore untrusted.
 * Always re-sanitizes so API-key fields cannot ride along.
 */
export function ensureEnrichmentSettings(
  token: EnrichConfigToken,
  options?: EnsureEnrichmentSettingsOptions,
): EnrichConfigToken {
  const slotModules = new Set(Object.values(token.slots).flat());
  const needsWikipedia = slotModules.has("wikipedia");

  const wikipediaExisting = asRecord(token.settings.wikipedia_settings) ?? {};
  const wikipediaSource = needsWikipedia
    ? {
        ...wikipediaExisting,
        ua_contact:
          stringField(wikipediaExisting, "ua_contact") ||
          "serp-renderer@system1.com",
      }
    : wikipediaExisting;

  const settings: Record<string, Record<string, unknown>> = {};
  for (const [key, value] of Object.entries(token.settings)) {
    if (key === "wikipedia_settings") continue;
    const sanitized = sanitizeModuleSettings(key, value);
    if (sanitized) settings[key] = sanitized;
  }

  if (needsWikipedia || Object.keys(wikipediaExisting).length > 0) {
    const wikipedia_settings = sanitizeModuleSettings(
      "wikipedia_settings",
      wikipediaSource,
    );
    if (wikipedia_settings) settings.wikipedia_settings = wikipedia_settings;
  }

  for (const [moduleName, spec] of Object.entries(MODULE_SETTINGS_DEFAULTS)) {
    if (!slotModules.has(moduleName)) continue;
    const existing = { ...(asRecord(token.settings[spec.settingsKey]) ?? {}) };
    // Token clock format is not a free string: EE only accepts world | us.
    // Drop anything else before overlay so a forged "24h" cannot beat the
    // normalized browser preference and then ride through sanitize.
    if (moduleName === "timezone") {
      const tokenFormat = normalizeDateAndTimeFormat(
        existing.date_and_time_format,
      );
      if (tokenFormat) existing.date_and_time_format = tokenFormat;
      else delete existing.date_and_time_format;
    }
    const preferred = normalizeDateAndTimeFormat(options?.dateAndTimeFormat);
    const merged = {
      ...spec.defaults,
      ...(preferred && moduleName === "timezone"
        ? { date_and_time_format: preferred }
        : {}),
      ...existing,
    };
    const sanitized = sanitizeModuleSettings(spec.settingsKey, merged);
    // Defaults always produce a non-empty sanitized object (e.g. date_and_time_format).
    settings[spec.settingsKey] = sanitized ?? { ...spec.defaults };
  }

  return { ...token, settings };
}

const REQUIRED_PROVIDER_SETTINGS: Record<string, string> = {
  finnhub: "finnhub_settings",
  finnhub_profile: "finnhub_profile_settings",
};

/**
 * Provider modules must carry a valid secret selection from their configuration.
 * In particular, never turn an unsigned slots-only browser token into a
 * credentialed request by supplying a renderer-owned default.
 *
 * This is fail-closed configuration validation, not token authentication:
 * PAPI still needs to sign ee_token to prevent forgery of an explicit path.
 */
export function hasRequiredProviderSettings(token: EnrichConfigToken): boolean {
  const slotModules = new Set(Object.values(token.slots).flat());
  for (const [moduleName, settingsKey] of Object.entries(
    REQUIRED_PROVIDER_SETTINGS,
  )) {
    if (!slotModules.has(moduleName)) continue;
    const moduleSettings = asRecord(token.settings[settingsKey]);
    if (!safeSecretName(stringField(moduleSettings, "secret_name"))) {
      return false;
    }
  }
  return true;
}

/**
 * Resolve EE config_token from PAPI ee_token (preferred), else layout modules.
 */
export function resolveConfigToken(
  response: PapiResponse | undefined,
  options?: EnsureEnrichmentSettingsOptions,
):
  | { source: "ee_token"; eeToken: string; configToken: EnrichConfigToken }
  | { source: "layout_modules"; configToken: EnrichConfigToken }
  | undefined {
  const dateAndTimeFormat =
    options?.dateAndTimeFormat ?? dateAndTimeFormatFromResponse(response);
  const ensureOptions = dateAndTimeFormat ? { dateAndTimeFormat } : undefined;

  const eeToken = extractEeToken(response);
  if (eeToken) {
    const decoded = decodeEeToken(eeToken);
    if (decoded) {
      const configToken = ensureEnrichmentSettings(decoded, ensureOptions);
      if (!hasRequiredProviderSettings(configToken)) return undefined;
      return {
        source: "ee_token",
        eeToken,
        configToken,
      };
    }
  }

  const fromLayout = buildConfigTokenFromSlots(
    extractEnrichmentSlots(response),
    ensureOptions,
  );
  if (fromLayout && hasRequiredProviderSettings(fromLayout)) {
    return { source: "layout_modules", configToken: fromLayout };
  }

  return undefined;
}

/**
 * Collect enrichment_slot placeholders from the layout/response.
 * Modules may be on the group or only in ee_token — empty modules are OK here
 * (used for debug / knowing which placeholders exist).
 */
export function extractEnrichmentSlots(
  response: PapiResponse | undefined,
): EnrichmentSlotDef[] {
  const out: EnrichmentSlotDef[] = [];
  if (!response) return out;

  for (const region of response.regions ?? []) {
    const regionName =
      typeof region.name === "string" && region.name ? region.name : "";
    for (const group of region.groups ?? []) {
      if (!isEnrichmentSlotGroup(group)) continue;
      const g = group as Record<string, unknown>;
      const name = typeof g.name === "string" ? g.name.trim() : "";
      if (!name) continue;
      out.push({
        name,
        modules: normalizeModules(g.modules),
        region: regionName,
      });
    }
  }

  return out;
}

/** Build EE config_token.slots from layout enrichment_slot groups that include modules. */
export function buildConfigTokenFromSlots(
  slotDefs: EnrichmentSlotDef[],
  options?: EnsureEnrichmentSettingsOptions,
): EnrichConfigToken | undefined {
  const withModules = slotDefs.filter((d) => d.modules.length > 0);
  if (withModules.length === 0) return undefined;

  const slots: Record<string, string[]> = {};
  for (const def of withModules) {
    slots[def.name] = def.modules;
  }

  return ensureEnrichmentSettings({ settings: {}, slots }, options);
}

/** Resolve search query from the page URL, then PAPI search_bar / page_options. */
export function resolveEnrichQuery(
  response: PapiResponse | undefined,
  pageUrl: string,
): string {
  try {
    const q = new URL(pageUrl, "http://localhost").searchParams.get("q");
    if (q?.trim()) return q.trim();
  } catch {
    /* ignore */
  }

  if (!response) return "";

  for (const region of response.regions ?? []) {
    for (const group of region.groups ?? []) {
      const g = group as Record<string, unknown>;
      const results = Array.isArray(g.results) ? g.results : [];
      for (const raw of results) {
        const r = asRecord(raw);
        const props = asRecord(r?.props) ?? r;
        const initial = stringField(props, "initial_query", "initialQuery");
        if (initial) return initial;
      }
    }
  }

  const pageOptions = asRecord(response.page_options);
  return stringField(pageOptions, "title", "query");
}

/** Map PAPI web-ish results into EE result_context.results. */
export function extractEnrichResults(
  response: PapiResponse | undefined,
): EnrichResultContext["results"] {
  const out: EnrichResultContext["results"] = [];
  if (!response) return out;

  const WEB_TYPES = new Set(["web", "web_results", "algo"]);

  for (const region of response.regions ?? []) {
    for (const group of region.groups ?? []) {
      const g = group as Record<string, unknown>;
      const resultType =
        typeof g.result_type === "string"
          ? g.result_type
          : typeof g.component === "string"
          ? g.component
          : "";
      if (!WEB_TYPES.has(resultType)) continue;

      const results = Array.isArray(g.results) ? g.results : [];
      for (const raw of results) {
        const r = asRecord(raw);
        if (!r) continue;
        const url = stringField(r, "url", "clickUrl", "click_url", "href");
        const title = stringField(r, "title", "title_name", "name");
        const desc = stringField(
          r,
          "description",
          "desc",
          "snippet",
          "abstract",
        );
        if (!url && !title) continue;
        out.push({ url, title, desc });
      }
    }
  }

  return out;
}

export function buildResultContext(
  response: PapiResponse | undefined,
  pageUrl: string,
): EnrichResultContext | undefined {
  const query = resolveEnrichQuery(response, pageUrl);
  if (!query) return undefined;

  const userDefaults = asRecord(response?.user_defaults);
  const language =
    stringField(userDefaults, "preferred_results_language", "language") || "en";
  const countryRaw = stringField(
    userDefaults,
    "preferred_results_region",
    "country_code",
  );
  const explicitCountry =
    countryRaw.length === 2 ? countryRaw.toUpperCase() : undefined;
  const country_code = explicitCountry ?? "US";

  const ctx: EnrichResultContext = {
    query,
    results: extractEnrichResults(response),
    language: language.slice(0, 2).toLowerCase() || "en",
    country_code,
  };
  // Timezone module: prefer an explicit 2-letter region for clock format. Do not
  // infer from "all" / missing region (mapped to US for other EE fields).
  const dateAndTimeFormat = dateAndTimeFormatFromResponse(response);
  if (dateAndTimeFormat) {
    ctx.date_and_time_format = dateAndTimeFormat;
  }
  return ctx;
}
